import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import type { FutebolVotacaoJogo }     from '@/shared/types';
import { useTvVotacao }                from '../hooks/useTvVotacao';

// ── Hook: detecta qual jogo acabou de receber um voto ─────────────────────────

function useVotoRecente(jogos: FutebolVotacaoJogo[]): string | null {
  const prevVotosRef = useRef<Record<string, number>>({});
  const [jogoIdVotado, setJogoIdVotado] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prev = prevVotosRef.current;
    let votadoId: string | null = null;

    for (const jogo of jogos) {
      const anterior = prev[jogo.id] ?? 0;
      if (jogo.votos > anterior) {
        votadoId = jogo.id;
        break;
      }
    }

    const next: Record<string, number> = {};
    for (const jogo of jogos) next[jogo.id] = jogo.votos;
    prevVotosRef.current = next;

    if (votadoId !== null) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setJogoIdVotado(votadoId);
      timerRef.current = setTimeout(() => setJogoIdVotado(null), 900);
    }
  }, [jogos]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return jogoIdVotado;
}

// ── Escudo com fallback de iniciais ──────────────────────────────────────────

function Escudo({
  src, nome, className, textClassName,
}: {
  src: string | null; nome: string; className: string; textClassName: string;
}) {
  const [erro, setErro] = useState(false);
  if (!src || erro) {
    return (
      <span className={`flex items-center justify-center rounded-full bg-white/15 text-white font-bold shrink-0 ${className} ${textClassName}`}>
        {nome.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <img src={src} alt={nome} className={`object-contain shrink-0 ${className}`} onError={() => setErro(true)} />
  );
}

// ── Partículas flutuantes de voto ─────────────────────────────────────────────

function VotoParticula({ ativo }: { ativo: boolean }) {
  if (!ativo) return null;
  const particulas: { x: number; y: number; delay: number; emoji: string }[] = [
    { x: -30, y: -50, delay: 0,   emoji: '⚽' },
    { x:  40, y: -60, delay: 80,  emoji: '✅' },
    { x: -50, y: -30, delay: 160, emoji: '⚽' },
    { x:  60, y: -40, delay: 40,  emoji: '✅' },
    { x:  10, y: -70, delay: 120, emoji: '⚽' },
    { x: -20, y: -55, delay: 200, emoji: '✅' },
  ];
  return (
    <>
      {particulas.map((p, i) => (
        <motion.span
          key={i}
          className="pointer-events-none absolute text-lg"
          style={{ left: '50%', top: '50%' }}
          initial={{ x: '-50%', y: '-50%', scale: 1, opacity: 1 }}
          animate={{ x: `calc(-50% + ${p.x}px)`, y: `calc(-50% + ${p.y}px)`, scale: 0, opacity: 0 }}
          transition={{ duration: 0.7, delay: p.delay / 1000, ease: 'easeOut' }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </>
  );
}

// ── Modo standalone: card grande ─────────────────────────────────────────────

function CardVotacaoTv({ jogo, totalVotos, isLider, rank, animando }: {
  jogo: FutebolVotacaoJogo; totalVotos: number; isLider: boolean; rank: number; animando: boolean;
}) {
  const pct = totalVotos > 0 ? Math.round((jogo.votos / totalVotos) * 100) : 0;
  const cor = (['bg-amber-400', 'bg-sky-400', 'bg-emerald-400'] as const)[rank] ?? 'bg-white/50';

  return (
    <div className={`relative rounded-2xl p-5 transition-all duration-500 ${
      isLider ? 'bg-white/15 ring-2 ring-amber-400 shadow-lg shadow-amber-400/20' : 'bg-white/8'
    }`}>
      {/* Pulse ring ao receber voto */}
      {animando && (
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          initial={{ boxShadow: '0 0 0 0px rgba(251,191,36,0.6)' }}
          animate={{ boxShadow: ['0 0 0 0px rgba(251,191,36,0.6)', '0 0 0 10px rgba(251,191,36,0)', '0 0 0 0px rgba(251,191,36,0)'] }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        />
      )}

      {isLider && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-amber-400 text-black text-xs font-black px-3 py-1 rounded-full whitespace-nowrap">
          👑 ESCOLHA DO SALÃO
        </div>
      )}
      {jogo.liga_nome && (
        <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-3 text-center">{jogo.liga_nome}</p>
      )}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex flex-col items-center gap-2 flex-1">
          <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} className="w-16 h-16" textClassName="text-lg" />
          <span className="text-sm font-bold text-white text-center leading-tight">{jogo.time_casa}</span>
        </div>
        <span className="text-2xl font-black text-white/30 shrink-0">×</span>
        <div className="flex flex-col items-center gap-2 flex-1">
          <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} className="w-16 h-16" textClassName="text-lg" />
          <span className="text-sm font-bold text-white text-center leading-tight">{jogo.time_visitante}</span>
        </div>
      </div>

      {/* Barra de progresso com ripple no novo voto */}
      <div className="relative w-full h-3 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${cor}`}
          style={{ width: `${pct}%` }}
        />
        {animando && (
          <motion.div
            className={`absolute inset-y-0 left-0 rounded-full ${cor}`}
            style={{ width: `${pct}%`, transformOrigin: 'left center' }}
            initial={{ scaleX: 1, opacity: 0.7 }}
            animate={{ scaleX: 1.15, opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        )}
      </div>
      <div className="flex justify-between mt-2">
        <motion.span
          className={`text-sm font-black ${isLider ? 'text-amber-400' : 'text-white/70'}`}
          style={{ display: 'inline-block' }}
          animate={animando ? { scale: [1, 1.35, 0.9, 1] } : { scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut', times: [0, 0.4, 0.7, 1] }}
        >
          {pct}%
        </motion.span>
        <motion.span
          className="text-sm text-white/50"
          style={{ display: 'inline-block' }}
          animate={animando ? { scale: [1, 1.35, 0.9, 1] } : { scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut', times: [0, 0.4, 0.7, 1] }}
        >
          {jogo.votos} {jogo.votos === 1 ? 'voto' : 'votos'}
        </motion.span>
      </div>
    </div>
  );
}

// ── Painel lateral: único jogo confirmado ────────────────────────────────────

function PainelConfirmacaoTv({ jogo }: { jogo: FutebolVotacaoJogo }) {
  return (
    <div className="fixed right-5 top-1/2 -translate-y-1/2 z-30 pointer-events-none w-64">
      <div className="bg-black/75 backdrop-blur-md rounded-2xl ring-1 ring-sky-500/30 p-4 flex flex-col gap-3">
        <div className="text-center">
          <p className="text-[9px] font-bold tracking-[0.18em] text-sky-400/50 uppercase mb-0.5">Em breve na TV</p>
          <p className="text-xs font-black text-white">📺 Transmissão confirmada</p>
        </div>
        {jogo.liga_nome && (
          <p className="text-[8px] font-semibold text-sky-400/50 uppercase tracking-widest text-center">{jogo.liga_nome}</p>
        )}
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} className="w-10 h-10" textClassName="text-[9px]" />
            <span className="text-[9px] font-semibold text-white/80 text-center leading-tight line-clamp-2 w-full">{jogo.time_casa}</span>
          </div>
          <span className="text-white/25 font-black text-xs shrink-0">×</span>
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} className="w-10 h-10" textClassName="text-[9px]" />
            <span className="text-[9px] font-semibold text-white/80 text-center leading-tight line-clamp-2 w-full">{jogo.time_visitante}</span>
          </div>
        </div>
        <div className="py-1.5 rounded-xl bg-sky-500/10 border border-sky-500/20 text-center">
          <p className="text-[9px] font-bold text-sky-400">✓ Único jogo disponível neste horário</p>
        </div>
      </div>
    </div>
  );
}

// ── Painel lateral: votação em curso ─────────────────────────────────────────

function PainelLateral({ jogos, totalVotos, lider, jogoIdVotado }: {
  jogos: FutebolVotacaoJogo[]; totalVotos: number;
  lider: FutebolVotacaoJogo | null; jogoIdVotado: string | null;
}) {
  const coresBarra = ['bg-amber-400', 'bg-sky-400', 'bg-emerald-400'] as const;

  return (
    <div className="fixed right-5 top-1/2 -translate-y-1/2 z-30 pointer-events-none w-64">
      <div className="bg-black/75 backdrop-blur-md rounded-2xl ring-1 ring-white/10 p-4 flex flex-col gap-3">
        <div className="text-center">
          <p className="text-[9px] font-bold tracking-[0.18em] text-white/35 uppercase mb-0.5">Vote no celular</p>
          <p className="text-xs font-black text-white">Qual jogo vai passar?</p>
        </div>
        <div className="flex flex-col gap-2">
          {jogos.map((jogo, i) => {
            const pct      = totalVotos > 0 ? Math.round((jogo.votos / totalVotos) * 100) : 0;
            const isLider  = lider?.id === jogo.id && jogo.votos > 0;
            const cor      = coresBarra[i] ?? 'bg-white/50';
            const animando = jogoIdVotado === jogo.id;
            return (
              <div
                key={jogo.id}
                className={`relative rounded-xl p-2.5 transition-all duration-500 ${
                  isLider ? 'bg-white/10 ring-1 ring-amber-400/40' : 'bg-white/5'
                }`}
              >
                {/* Pulse ring ao receber voto */}
                {animando && (
                  <motion.div
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    initial={{ boxShadow: '0 0 0 0px rgba(251,191,36,0.6)' }}
                    animate={{ boxShadow: ['0 0 0 0px rgba(251,191,36,0.6)', '0 0 0 10px rgba(251,191,36,0)', '0 0 0 0px rgba(251,191,36,0)'] }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                  />
                )}

                {/* Partículas flutuantes */}
                <VotoParticula ativo={animando} />

                {isLider && <p className="text-[8px] font-black text-amber-400 uppercase tracking-widest mb-1">👑 Liderando</p>}
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                    <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} className="w-7 h-7" textClassName="text-[8px]" />
                    <span className="text-[9px] font-semibold text-white/80 text-center leading-tight line-clamp-2 w-full">{jogo.time_casa}</span>
                  </div>
                  <span className="text-white/25 font-black text-xs shrink-0">×</span>
                  <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                    <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} className="w-7 h-7" textClassName="text-[8px]" />
                    <span className="text-[9px] font-semibold text-white/80 text-center leading-tight line-clamp-2 w-full">{jogo.time_visitante}</span>
                  </div>
                </div>
                {jogo.liga_nome && <p className="text-[8px] text-white/25 uppercase tracking-wide truncate mb-1">{jogo.liga_nome}</p>}

                {/* Barra com ripple */}
                <div className="relative w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${cor}`} style={{ width: `${pct}%` }} />
                  {animando && (
                    <motion.div
                      className={`absolute inset-y-0 left-0 rounded-full ${cor}`}
                      style={{ width: `${pct}%`, transformOrigin: 'left center' }}
                      initial={{ scaleX: 1, opacity: 0.7 }}
                      animate={{ scaleX: 1.15, opacity: 0 }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  )}
                </div>
                <div className="flex justify-between mt-1">
                  <motion.span
                    className={`text-[10px] font-bold ${isLider ? 'text-amber-400' : 'text-white/50'}`}
                    style={{ display: 'inline-block' }}
                    animate={animando ? { scale: [1, 1.35, 0.9, 1] } : { scale: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut', times: [0, 0.4, 0.7, 1] }}
                  >
                    {pct}%
                  </motion.span>
                  <motion.span
                    className="text-[10px] text-white/25"
                    style={{ display: 'inline-block' }}
                    animate={animando ? { scale: [1, 1.35, 0.9, 1] } : { scale: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut', times: [0, 0.4, 0.7, 1] }}
                  >
                    {jogo.votos}v
                  </motion.span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-[9px] text-white/20">{totalVotos} {totalVotos === 1 ? 'voto' : 'votos'} registrados</p>
      </div>
    </div>
  );
}

// ── Painel lateral: anúncio do vencedor ──────────────────────────────────────

const DURACAO_ANUNCIO_MS = 12_000;

const CONFETES = [
  { left: '15%', top: '8%',  delay: '0s',    color: '#fbbf24' },
  { left: '30%', top: '5%',  delay: '0.15s', color: '#34d399' },
  { left: '50%', top: '3%',  delay: '0.05s', color: '#f87171' },
  { left: '65%', top: '7%',  delay: '0.25s', color: '#60a5fa' },
  { left: '80%', top: '4%',  delay: '0.1s',  color: '#fbbf24' },
  { left: '22%', top: '12%', delay: '0.3s',  color: '#a78bfa' },
  { left: '72%', top: '10%', delay: '0.2s',  color: '#34d399' },
];

function AnuncioVencedorTv({ jogo }: { jogo: FutebolVotacaoJogo }) {
  const [visivel, setVisivel] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setVisivel(false), DURACAO_ANUNCIO_MS);
    return () => clearTimeout(t);
  }, [jogo.id]);

  if (!visivel) return null;

  return (
    <div className="fixed right-5 top-1/2 -translate-y-1/2 z-30 pointer-events-none w-68">
      <motion.div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(10,8,4,0.96) 0%, rgba(20,14,2,0.94) 100%)',
          border: '1.5px solid rgba(251,191,36,0.5)',
        }}
        initial={{ x: 80, opacity: 0, scale: 0.85 }}
        animate={{
          x: 0,
          opacity: 1,
          scale: 1,
          boxShadow: [
            '0 0 0 0px rgba(251,191,36,0), 0 8px 40px rgba(0,0,0,0.7)',
            '0 0 0 6px rgba(251,191,36,0.25), 0 8px 40px rgba(0,0,0,0.7)',
            '0 0 0 0px rgba(251,191,36,0), 0 8px 40px rgba(0,0,0,0.7)',
          ],
        }}
        transition={{
          x:          { type: 'spring', damping: 20, stiffness: 200 },
          opacity:    { type: 'spring', damping: 20, stiffness: 200 },
          scale:      { type: 'spring', damping: 20, stiffness: 200 },
          boxShadow:  { duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
        }}
      >
        {/* Confetes */}
        {CONFETES.map((c, i) => (
          <motion.span
            key={i}
            style={{
              position:     'absolute',
              width:        '7px',
              height:       '7px',
              borderRadius: '2px',
              left:         c.left,
              top:          c.top,
              background:   c.color,
            }}
            initial={{ y: 0, rotate: 0, opacity: 1 }}
            animate={{ y: 80, rotate: 720, opacity: 0 }}
            transition={{ duration: 1.5, delay: parseFloat(c.delay), ease: 'easeIn', repeat: Infinity, repeatType: 'loop' }}
          />
        ))}

        {/* Cabeçalho âmbar */}
        <div className="flex items-center justify-center gap-2 px-4 pt-4 pb-2">
          <motion.span
            className="text-xl"
            style={{ display: 'inline-block' }}
            animate={{ rotate: [-8, 8, -8], scale: [1, 1.2, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            👑
          </motion.span>
          <div className="text-center">
            <p className="text-[10px] font-black text-amber-400 uppercase tracking-[0.2em] leading-tight">Escolha do Salão!</p>
            <p className="text-[8px] text-amber-400/50 tracking-wide">O salão decidiu</p>
          </div>
          <motion.span
            className="text-xl"
            style={{ display: 'inline-block' }}
            animate={{ rotate: [-8, 8, -8], scale: [1, 1.2, 1] }}
            transition={{ duration: 1.4, delay: 0.7, repeat: Infinity, ease: 'easeInOut' }}
          >
            👑
          </motion.span>
        </div>

        {/* Times */}
        <div className="flex items-center justify-center gap-3 px-4 pb-3">
          <div className="flex flex-col items-center gap-1.5 flex-1">
            <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} className="w-12 h-12" textClassName="text-sm" />
            <span className="text-[9px] font-bold text-white/90 text-center leading-tight line-clamp-2 w-full">{jogo.time_casa}</span>
          </div>
          <span className="text-white/25 font-black text-base shrink-0">×</span>
          <div className="flex flex-col items-center gap-1.5 flex-1">
            <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} className="w-12 h-12" textClassName="text-sm" />
            <span className="text-[9px] font-bold text-white/90 text-center leading-tight line-clamp-2 w-full">{jogo.time_visitante}</span>
          </div>
        </div>

        {jogo.liga_nome && (
          <p className="text-center text-[8px] text-amber-400/30 uppercase tracking-widest pb-1">{jogo.liga_nome}</p>
        )}

        {/* Confirmação */}
        <div className="mx-4 mb-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
          <p className="text-[10px] font-bold text-emerald-400">✓ Esse jogo vai passar na TV!</p>
          <p className="text-[8px] text-white/20 mt-0.5">{jogo.votos} {jogo.votos === 1 ? 'voto' : 'votos'}</p>
        </div>

        {/* Barra de progresso auto-hide */}
        <div className="h-0.5 bg-white/5">
          <motion.div
            className="h-full bg-amber-400/60 rounded-full"
            style={{ transformOrigin: 'left center' }}
            initial={{ scaleX: 1 }}
            animate={{ scaleX: 0 }}
            transition={{ duration: DURACAO_ANUNCIO_MS / 1000, ease: 'linear' }}
          />
        </div>
      </motion.div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function TvPreGameVoting({ telaId, asOverlay = false }: { telaId: string; asOverlay?: boolean }) {
  const { jogos, loading, totalVotos, lider, vencedor } = useTvVotacao(telaId);
  const jogoIdVotado = useVotoRecente(jogos);

  if (asOverlay) {
    if (loading) return null;
    if (vencedor) return <AnuncioVencedorTv jogo={vencedor} />;
    if (jogos.length === 0) return null;
    if (jogos.length === 1) return <PainelConfirmacaoTv jogo={jogos[0]} />;
    return <PainelLateral jogos={jogos} totalVotos={totalVotos} lider={lider} jogoIdVotado={jogoIdVotado} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-950">
        <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (jogos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full bg-gray-950 text-white/40">
        <span className="text-6xl mb-4">⚽</span>
        <p className="text-lg font-semibold">Votação não iniciada</p>
      </div>
    );
  }

  if (jogos.length === 1) {
    const jogo = jogos[0];
    return (
      <div className="flex flex-col items-center justify-center w-full h-full bg-gray-950 text-white p-8 gap-6">
        <div className="text-center">
          <p className="text-xs font-bold tracking-[0.3em] text-sky-400/60 uppercase mb-1">Em breve na TV do salão</p>
          <h1 className="text-3xl font-black tracking-tight">📺 Transmissão confirmada</h1>
        </div>
        <div className="w-full max-w-sm rounded-2xl bg-white/5 ring-1 ring-sky-500/20 p-6 flex flex-col gap-4">
          {jogo.liga_nome && (
            <p className="text-xs font-semibold text-sky-400/60 uppercase tracking-widest text-center">{jogo.liga_nome}</p>
          )}
          <div className="flex items-center justify-between gap-6">
            <div className="flex flex-col items-center gap-2 flex-1">
              <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} className="w-20 h-20" textClassName="text-xl" />
              <span className="text-sm font-bold text-white text-center leading-tight">{jogo.time_casa}</span>
            </div>
            <span className="text-2xl font-black text-white/20 shrink-0">×</span>
            <div className="flex flex-col items-center gap-2 flex-1">
              <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} className="w-20 h-20" textClassName="text-xl" />
              <span className="text-sm font-bold text-white text-center leading-tight">{jogo.time_visitante}</span>
            </div>
          </div>
          <div className="py-2.5 rounded-xl bg-sky-500/10 border border-sky-500/20 text-center">
            <p className="text-sm font-bold text-sky-400">✓ Único jogo disponível neste horário</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-gray-950 text-white p-8 gap-6">
      <div className="text-center">
        <p className="text-xs font-bold tracking-[0.3em] text-white/40 uppercase mb-1">Vote agora pelo seu celular</p>
        <h1 className="text-3xl font-black tracking-tight">📺 Qual jogo vai passar?</h1>
      </div>
      <div className="flex flex-col gap-8 flex-1">
        {jogos.map((jogo, i) => (
          <CardVotacaoTv
            key={jogo.id}
            jogo={jogo}
            totalVotos={totalVotos}
            isLider={lider?.id === jogo.id && jogo.votos > 0}
            rank={i}
            animando={jogoIdVotado === jogo.id}
          />
        ))}
      </div>
      <p className="text-center text-white/30 text-sm">
        {totalVotos} {totalVotos === 1 ? 'voto' : 'votos'} registrados
      </p>
    </div>
  );
}
